// react
import { useContext, useEffect, useId } from 'react';

// hooks
import { useLatestRef } from 'components/shared/hooks/useLatestRef';

// types
import type { ShortcutAction } from './ShortcutProvider.types';

// utils
import { ShortcutContext } from './ShortcutContext';
import {
  isValidActivatorKey,
  shortcutSetFingerprint
} from './ShortcutProvider.utils';

export const useCreateShortcutContext = (
  context: string,
  shortcuts: ShortcutAction[]
) => {
  'use memo';

  const { disableShortcut, enableShortcut, register, unregister } =
    useContext(ShortcutContext);

  // Keep the newest definitions reachable from the registered callbacks. Callers build
  // `shortcuts` inline, so its callbacks are new objects on every render; `useLatestRef`
  // hands out one stable ref holding the newest set, so the effect below can depend on it
  // without a fresh callback identity ever invalidating it.
  const latestShortcuts = useLatestRef(shortcuts);

  // Registration only depends on the *shape* of the shortcut set — its names, key
  // combinations, labels and active flags. `shortcutSetFingerprint` reduces that to a
  // string, so the dependency array below is honest and exhaustive without re-registering
  // every render, and callers do not have to declare a trigger list of their own.
  const fingerprint = shortcutSetFingerprint(shortcuts);

  useEffect(() => {
    const registeredNames = latestShortcuts.current.flatMap(
      ({
        name,
        label,
        description,
        activatorKey,
        controllerKeys,
        active,
        hidden,
        repeatable
      }) => {
        // Sanity check, should not be possible due to type restrictions
        if (!isValidActivatorKey(activatorKey)) {
          console.error(`Invalid activatorKey used: ${activatorKey}`);
          return [];
        }

        register?.({
          context,
          name,
          label,
          description,
          activatorKey,
          controllerKeys,
          active,
          hidden,
          repeatable,
          // Resolved at keypress time so the shortcut always runs the callback from the
          // most recent render, without re-registering when that callback changes.
          callback: (...args) =>
            latestShortcuts.current
              .find((shortcut) => shortcut.name === name)
              ?.callback(...args)
        });

        return name;
      }
    );

    // Unregister by name rather than by context: several components can share one context
    // name, and they must not take each other's shortcuts down with them.
    return () => registeredNames.forEach((name) => unregister?.(context, name));
  }, [context, fingerprint, latestShortcuts, register, unregister]);

  return { disableShortcut, enableShortcut, unregister };
};

/**
 * Narrows the active shortcut contexts down to `contexts` while `isNarrowed` holds, and puts
 * back whatever was active before afterwards. That is what a component does while it owns the
 * keyboard -- a popover or a form is open, and the shortcuts behind it must not fire.
 *
 * Pass no `contexts` to disable every shortcut, or e.g. `[GLOBAL_CONTEXT]` to keep one alive.
 *
 * Owners nest. A popover inside the appointment form restores the *form's* narrowing when it
 * closes, not every context -- so the calendar behind the form stays deaf to the arrow keys.
 * The narrowing is also dropped if the owner unmounts while it still holds one.
 */
export const useNarrowedShortcutContexts = (
  isNarrowed: boolean,
  contexts: string[] = []
) => {
  'use memo';

  const { pushNarrowing, popNarrowing } = useContext(ShortcutContext);

  // Identifies this owner's entry on the provider's narrowing stack for as long as it is
  // mounted. `useId` rather than a counter so two instances of one component never collide.
  const id = useId();

  // Callers pass the context list inline, so its identity changes every render. The ref hands
  // the list itself to `pushNarrowing`, and its JSON form keys the effect: serialising rather
  // than joining keeps a name containing the separator from splitting into two contexts.
  const latestContexts = useLatestRef(contexts);
  const serialisedContexts = JSON.stringify(contexts);

  useEffect(() => {
    if (!isNarrowed) return;

    pushNarrowing?.(id, latestContexts.current);

    return () => popNarrowing?.(id);
  }, [
    id,
    isNarrowed,
    latestContexts,
    popNarrowing,
    pushNarrowing,
    serialisedContexts
  ]);
};
